/**
 * AgentFlow API Service
 *
 * Provides API integration for AgentFlow workflows like OrganizeFiles.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FileMove {
  file_name: string;
  target_collection_name: string;
  new_name?: string | null;
  artifact_id?: string | null; // resolved internally, not needed in requests
}

export interface ProposedOrganization {
  collections_to_create: string[];
  file_moves: FileMove[];
  summary: string;
}

export interface StartOrganizeFilesRequest {
  user_instructions?: string | null;
  require_approval?: boolean;
  approval_timeout_hours?: number;
}

export interface StartOrganizeFilesResponse {
  workflow_id: string;
  matter_id: string;
  message: string;
}

export interface OrganizeFilesStatusResponse {
  workflow_id: string;
  status:
    | 'initializing'
    | 'analyzing'
    | 'awaiting_approval'
    | 'applying'
    | 'completed'
    | 'rejected'
    | 'timed_out'
    | 'failed';
  awaiting_approval: boolean;
  has_proposal: boolean;
  proposed_organization: ProposedOrganization | null;
  approval_action: string | null;
  apply_result?: {
    success: boolean;
    collections_created: string[];
    files_moved: number;
    files_renamed: number;
    errors: string[];
  } | null;
}

export interface SubmitApprovalRequest {
  action: 'approve' | 'reject' | 'request_changes';
  reason?: string | null;
  modified_plan?: ProposedOrganization | null;
}

export interface SubmitApprovalResponse {
  ok: boolean;
}

// ---------------------------------------------------------------------------
// LOA Chaser Types
// ---------------------------------------------------------------------------

/** Generic execution plan step from any AgentFlow workflow */
export interface ExecutionPlanStep {
  step_index: number;
  step_id: string;
  step_type: string;
  delay_days: number;
  label?: string | null;
  is_current: boolean;
  is_completed?: boolean;
}

export interface StartLOAChaserRequest {
  /** Task ID - all call configuration is derived from VoiceAIAgentStrategy */
  task_id: string;
  /** Optional flow preset ID for LOA chaser */
  flow_id?: string | null;
  /** Front conversation ID for follow-up emails */
  email_thread_id?: string | null;
  /** Recipient email when no thread exists */
  recipient_email?: string | null;
  /** Phone number for Voice AI call (optional override, defaults to strategy-derived number) */
  phone_number?: string | null;
  /** Adjuster phone number for Dialpad calls (optional) */
  adjuster_phone?: string | null;
  /** Index of the step to start from (0-based, default: 0 = first step) */
  starting_step_index?: number;
  /** If true, skip the delay for the first executed step and run immediately */
  skip_initial_delay?: boolean;
  /** If true, start at the wait period before the starting step (execute the delay first) */
  start_in_wait?: boolean;
  /** Document instance IDs for LOR documents to attach to initial email */
  lor_document_ids?: string[] | null;
  /** Override subject line for email steps (applies to new threads only) */
  email_subject_override?: string | null;
  /** Email content for flow steps. Maps step_id to {subject, body} */
  email_step_content?: Record<string, { subject: string; body: string }> | null;
}

export interface StartLOAChaserResponse {
  workflow_id: string;
  matter_id: string;
  coverage_id: string;
  message: string;
}

export interface LOAChaserStatusResponse {
  workflow_id: string;
  traces: Array<Record<string, unknown>>;
  /** Workflow execution status: running, completed, terminated, failed, not_found */
  workflow_status?: string;
  /** The input provided when starting the workflow */
  workflow_input?: Record<string, unknown> | null;
  /** Workflow start time (ISO string) */
  start_time?: string | null;
  /** Workflow close time (ISO string) */
  close_time?: string | null;
  /** Execution plan from the workflow (current and future steps) */
  execution_plan?: ExecutionPlanStep[];
  /** Last step index the workflow was on (for terminated/cancelled workflows) */
  last_step_index?: number | null;
}

export interface JumpToStepRequest {
  /** Task ID of the running workflow */
  task_id: string;
  /** Step index to jump to (0-based) */
  step_index: number;
}

export interface JumpToStepResponse {
  ok: boolean;
  message: string;
}

export interface CancelLOAChaserRequest {
  /** Task ID of the running workflow */
  task_id: string;
  /** Optional reason for cancellation */
  reason?: string | null;
}

export interface CancelLOAChaserResponse {
  ok: boolean;
  message: string;
}

/** Email content for a single step in a flow */
export interface EmailStepContent {
  step_id: string;
  step_label: string;
  subject: string;
  body: string;
}

/** LOA Chaser flow configuration */
export interface LOAChaserFlowConfig {
  flow_id: string;
  steps: ExecutionPlanStep[];
  email_steps: EmailStepContent[];
}

/** Response listing available LOA Chaser flows */
export interface LOAChaserFlowsResponse {
  flows: LOAChaserFlowConfig[];
  default_flow_id: string;
}

export interface WorkflowExecutionSummary {
  workflow_id: string;
  run_id: string;
  status: string; // running, completed, terminated, failed, etc.
  start_time?: string | null;
  started_at?: string | null;
  close_time?: string | null;
}

export interface LOAChaserHistoryResponse {
  executions: WorkflowExecutionSummary[];
  has_running: boolean;
}

// ---------------------------------------------------------------------------
// Crashing Out Types
// ---------------------------------------------------------------------------

export interface StartCrashingOutRequest {
  instructions?: string | null;
  model?: string;
  max_tool_iterations?: number;
}

export interface StartCrashingOutResponse {
  workflow_id: string;
  matter_id: string;
  coverage_id: string;
  message: string;
}

export interface CancelCrashingOutRequest {
  reason?: string | null;
}

export interface CancelCrashingOutResponse {
  ok: boolean;
  message: string;
}

export interface CrashingOutStatusResponse {
  workflow_id: string;
  traces: Array<Record<string, unknown>>;
  workflow_status?: string;
  workflow_input?: Record<string, unknown> | null;
  start_time?: string | null;
  close_time?: string | null;
  final_response?: string | null;
  error?: string | null;
  error_type?: string | null;
  last_step_index?: number | null;
}

export interface CrashingOutHistoryResponse {
  executions: WorkflowExecutionSummary[];
  has_running: boolean;
}

export interface CrashingOutPendingReview {
  approval_id: string;
  response_schema?: Record<string, unknown> | null;
  form_title?: string | null;
  form_description?: string | null;
  created_at?: string | null;
  source_workflow_id?: string | null;
  source_run_id?: string | null;
}

export interface CrashingOutPendingReviewsResponse {
  reviews: CrashingOutPendingReview[];
}

export type CrashingOutSource = 'temporal' | 'postgres';

// ---------------------------------------------------------------------------
// LOA Scan Types (hidden admin page)
// ---------------------------------------------------------------------------

export interface LOAScanLatestResponse {
  scan_ts: number | null;
}

export interface LOAScanStartResponse {
  scan_ts: number;
}

export interface LOAScanResultItem {
  task_id: string;
  matter_id: string;
  matter_name: string;
  carrier: string | null;
  task_created_at: string | null;
  workflow_id: string;
  status: 'running' | 'completed' | 'failed' | 'not_found';
  action: string | null;
  confidence: number | null;
  reason: string | null;
  evidence: Array<Record<string, unknown>>;
  tool_calls: number;
}

export interface LOAScanResultsResponse {
  scan_ts: number;
  /** If true, the workflow has been garbage-collected and is no longer available. */
  scan_unavailable?: boolean;
  total_tasks?: number;
  skipped_running?: number;
  skipped_no_matter?: number;
  started?: number;
  start_errors?: number;
  start_phase_complete?: boolean;
  results?: LOAScanResultItem[];
  completed?: number;
  still_running?: number;
  failed?: number;
  scan_complete?: boolean;
}

export interface LOAScanApplyRequest {
  scan_ts: number;
  task_ids: string[];
}

export interface LOAScanApplyResponse {
  applied: number;
  errors: number;
  details: Array<Record<string, unknown>>;
}

// ---------------------------------------------------------------------------
// Records Request Evaluation Types
// ---------------------------------------------------------------------------

export type RecordsRequestStatusType =
  | 'running'
  | 'completed'
  | 'ready'
  | 'blocked'
  | 'draft'
  | 'failed'
  | 'needs_rebuild'
  | 'needs_re_evaluation'
  | 'submitting'
  | 'submitted'
  | 'not_found';

export type RecordsRequestOutcome =
  | 'ready_for_submit_review'
  | 'blocked_for_human_review'
  | 'failed';

export interface RecordsRequestStatusResponse {
  status: RecordsRequestStatusType;
  workflow_id?: string;
  step_index?: number;
  step_label?: string;
  total_steps?: number;
  started_at?: string;
  evaluation_run_id?: string;
  outcome?: RecordsRequestOutcome;
  ready_to_submit?: boolean;
  blocker_count?: number;
  placeholder_count?: number;
  page_count?: number;
  completed_at?: string;
}

export interface RecordsRequestDestination {
  channel: string;
  recipient_fax_number?: string | null;
  recipient_email?: string | null;
  recipient_mailing_address?: string | null;
  provider_name?: string | null;
  verified: boolean;
  alternatives: Array<Record<string, unknown>>;
}

export interface RecordsRequestPackageComponent {
  component_type: string;
  label: string;
  page_count?: number | null;
  signed?: boolean;
}

export interface RecordsRequestFieldItem {
  field_name: string;
  value: string | null;
  status: 'resolved' | 'unresolved';
}

export interface RecordsRequestFields {
  total: number;
  resolved: number;
  unresolved: number;
  items: RecordsRequestFieldItem[];
}

export interface RecordsRequestEvaluationDetail {
  id: string;
  outcome: RecordsRequestOutcome | null;
  ready_to_submit: boolean | null;
  completed_at: string | null;
  destination: RecordsRequestDestination | null;
  package_contents: RecordsRequestPackageComponent[];
  fields: RecordsRequestFields;
  blockers: string[];
  warnings: string[];
  pdf_url: string | null;
  compiled_pdf_blob_id?: string | null;
}

export interface RecordsRequestPromoteRequest {
  open_submission_flow?: boolean;
}

export interface RecordsRequestPromoteResponse {
  package_id: string;
  message: string;
}

export interface RecordsRequestStartRequest {
  instructions?: string | null;
}

export interface RecordsRequestStartResponse {
  workflow_id: string;
  submission_attempt_id?: string | null;
  message: string;
}

// ---------------------------------------------------------------------------
// Submission Attempt Types
// ---------------------------------------------------------------------------

export type SubmissionAttemptStatus =
  | 'draft'
  | 'ready'
  | 'blocked'
  | 'failed'
  | 'needs_rebuild'
  | 'needs_re_evaluation'
  | 'submitting'
  | 'submitted';

export interface SubmissionAttemptDestinationOption {
  channel: string;
  value: string | null;
  provider_name: string | null;
  verified: boolean;
  evidence_summary: string | null;
}

export type SubmissionAttemptDestinationState =
  | 'resolved'
  | 'conflict'
  | 'missing';

export interface SubmissionAttemptDestination {
  state: SubmissionAttemptDestinationState;
  recommended: SubmissionAttemptDestinationOption | null;
  alternatives: SubmissionAttemptDestinationOption[];
  agent_research_summary: string | null;
  manual_resolution: SubmissionAttemptDestinationOption | null;
}

export interface SubmissionAttemptSignature {
  strategy: string | null;
  resolved: boolean;
  agent_search_summary: string | null;
  manual_resolution: string | null;
}

export interface SubmissionAttemptPackageComponent {
  component_type: string;
  label: string;
  page_count: number | null;
  signed: boolean;
  component_order: number | null;
  start_page: number | null;
  end_page: number | null;
  metadata: Record<string, string>;
}

export type SubmissionAttemptFieldDisposition =
  | 'resolved'
  | 'blocking'
  | 'requires_confirmation'
  | 'non_blocking';

export interface SubmissionAttemptFieldItem {
  field_name: string;
  value: string | null;
  disposition: SubmissionAttemptFieldDisposition;
  agent_note: string | null;
  field_id: string | null;
}

export interface SubmissionAttemptFields {
  total: number;
  resolved: number;
  items: SubmissionAttemptFieldItem[];
}

export type SubmissionAttemptPipelineStepStatus =
  | 'completed'
  | 'blocked'
  | 'running'
  | 'pending';

export interface SubmissionAttemptPipelineStep {
  step_id: string;
  label: string;
  status: SubmissionAttemptPipelineStepStatus;
}

export interface SubmissionAttemptBlockerItem {
  category: string;
  summary: string;
  recommended_action?: string | null;
  page_number?: number | null;
  component_order?: number | null;
  component_type?: string | null;
  field_id?: string | null;
}

export interface GetSubmissionAttemptDetailResponse {
  id: string;
  status: SubmissionAttemptStatus;
  revision: number;
  destination: SubmissionAttemptDestination | null;
  signature: SubmissionAttemptSignature | null;
  package_contents: SubmissionAttemptPackageComponent[];
  fields: SubmissionAttemptFields;
  pipeline_steps: SubmissionAttemptPipelineStep[];
  blocker_count: number;
  blockers: string[];
  blocker_items: SubmissionAttemptBlockerItem[];
  warnings: string[];
  reviewer_summary: string | null;
  recommended_next_action: string | null;
  pdf_url: string | null;
  completed_at: string | null;
}

export type SubmissionAttemptActionType =
  | 'set_field_value'
  | 'confirm_field'
  | 'choose_destination'
  | 'set_signature_asset'
  | 'select_signed_document'
  | 'dismiss_field';

export interface SubmissionAttemptPatchRequest {
  action_type: SubmissionAttemptActionType;
  payload: Record<string, unknown>;
}

export interface SubmissionAttemptStartResponse {
  submission_attempt: GetSubmissionAttemptDetailResponse;
  package_id: string;
}

export interface SubmitSubmissionAttemptResponse {
  submission_attempt: GetSubmissionAttemptDetailResponse;
  message: string;
}

export interface RecordsRequestBatchStatusItem {
  treatment_request_id: string;
  status: string | null;
  blocker_count: number | null;
  ready_to_submit: boolean | null;
}

export interface RecordsRequestBatchStatusResponse {
  statuses: RecordsRequestBatchStatusItem[];
}

// ---------------------------------------------------------------------------
// API Service
// ---------------------------------------------------------------------------

export const agentflowApiClient = createApi({
  reducerPath: 'agentflowApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'OrganizeFilesWorkflow',
    'LOAChaserWorkflow',
    'CrashingOutWorkflow',
    'RecordsRequestWorkflow',
    'SubmissionAttempt',
  ] as const,
  endpoints: (builder) => ({
    /**
     * Start the OrganizeFiles workflow for a matter.
     * This workflow analyzes files and proposes an organization structure.
     */
    startOrganizeFiles: builder.mutation<
      StartOrganizeFilesResponse,
      { matterId: string; request?: StartOrganizeFilesRequest }
    >({
      query: ({ matterId, request = {} }) => ({
        url: `api/matters/${matterId}/agentflow/organize-files/start/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'OrganizeFilesWorkflow', id: matterId },
      ],
    }),

    /**
     * Get the current status of an OrganizeFiles workflow.
     */
    getOrganizeFilesStatus: builder.query<
      OrganizeFilesStatusResponse,
      { matterId: string }
    >({
      query: ({ matterId }) =>
        `api/matters/${matterId}/agentflow/organize-files/status/`,
      providesTags: (result, error, { matterId }) => [
        { type: 'OrganizeFilesWorkflow', id: matterId },
      ],
    }),

    /**
     * Submit an approval decision for a workflow awaiting human approval.
     */
    submitOrganizeFilesApproval: builder.mutation<
      SubmitApprovalResponse,
      { matterId: string; request: SubmitApprovalRequest }
    >({
      query: ({ matterId, request }) => ({
        url: `api/matters/${matterId}/agentflow/organize-files/approve/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'OrganizeFilesWorkflow', id: matterId },
      ],
    }),

    // -------------------------------------------------------------------------
    // LOA Chaser Endpoints
    // -------------------------------------------------------------------------

    /**
     * Start the LOA Chaser phone call workflow for a coverage.
     * This workflow initiates a phone call to the carrier and waits for completion.
     */
    startLOAChaser: builder.mutation<
      StartLOAChaserResponse,
      { matterId: string; coverageId: string; request: StartLOAChaserRequest }
    >({
      query: ({ matterId, coverageId, request }) => ({
        url: `api/matters/${matterId}/coverage/${coverageId}/agentflow/loa-chaser/start/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { coverageId }) => [
        { type: 'LOAChaserWorkflow', id: coverageId },
      ],
    }),

    /**
     * Get the current status of an LOA Chaser workflow.
     * Use taskId parameter for new task-based workflow format.
     */
    getLOAChaserStatus: builder.query<
      LOAChaserStatusResponse,
      { matterId: string; coverageId: string; taskId?: string }
    >({
      query: ({ matterId, coverageId, taskId }) => {
        const baseUrl = `api/matters/${matterId}/coverage/${coverageId}/agentflow/loa-chaser/status/`;
        return taskId ? `${baseUrl}?task_id=${taskId}` : baseUrl;
      },
      providesTags: (result, error, { coverageId, taskId }) => [
        { type: 'LOAChaserWorkflow', id: taskId || coverageId },
      ],
    }),

    /**
     * Jump to a specific step in a running LOA Chaser workflow.
     * This will skip remaining delay and any steps between current and target.
     */
    jumpToStep: builder.mutation<
      JumpToStepResponse,
      { matterId: string; coverageId: string; request: JumpToStepRequest }
    >({
      query: ({ matterId, coverageId, request }) => ({
        url: `api/matters/${matterId}/coverage/${coverageId}/agentflow/loa-chaser/jump/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { coverageId, request }) => [
        { type: 'LOAChaserWorkflow', id: request.task_id || coverageId },
      ],
    }),

    /**
     * List all workflow executions (history) for a LOA Chaser task.
     * Returns all runs including completed, terminated, and running.
     */
    getLOAChaserHistory: builder.query<
      LOAChaserHistoryResponse,
      { matterId: string; coverageId: string; taskId: string }
    >({
      query: ({ matterId, coverageId, taskId }) =>
        `api/matters/${matterId}/coverage/${coverageId}/agentflow/loa-chaser/history/?task_id=${taskId}`,
      providesTags: (result, error, { taskId }) => [
        { type: 'LOAChaserWorkflow', id: `history-${taskId}` },
      ],
    }),

    /**
     * Cancel a running LOA Chaser workflow.
     * Use this when a workflow gets stuck or is no longer needed.
     */
    cancelLOAChaser: builder.mutation<
      CancelLOAChaserResponse,
      { matterId: string; coverageId: string; request: CancelLOAChaserRequest }
    >({
      query: ({ matterId, coverageId, request }) => ({
        url: `api/matters/${matterId}/coverage/${coverageId}/agentflow/loa-chaser/cancel/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, { coverageId, request }) => [
        { type: 'LOAChaserWorkflow', id: request.task_id || coverageId },
      ],
    }),

    /**
     * Get status for a specific workflow run by run_id.
     * Use this to view details of historical runs.
     */
    getLOAChaserRunStatus: builder.query<
      LOAChaserStatusResponse,
      { matterId: string; coverageId: string; taskId: string; runId: string }
    >({
      query: ({ matterId, coverageId, taskId, runId }) =>
        `api/matters/${matterId}/coverage/${coverageId}/agentflow/loa-chaser/runs/${runId}/?task_id=${taskId}`,
      providesTags: (_result, _error, { runId }) => [
        { type: 'LOAChaserWorkflow', id: `run-${runId}` },
      ],
    }),

    // -------------------------------------------------------------------------
    // Crashing Out Endpoints
    // -------------------------------------------------------------------------

    /**
     * Start Crashing Out workflow for an approved coverage.
     */
    startCrashingOut: builder.mutation<
      StartCrashingOutResponse,
      { matterId: string; coverageId: string; request?: StartCrashingOutRequest }
    >({
      query: ({ matterId, coverageId, request = {} }) => ({
        url: `api/matters/${matterId}/coverage/${coverageId}/agentflow/crashing-out/start/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_result, _error, { coverageId }) => [
        { type: 'CrashingOutWorkflow', id: coverageId },
      ],
    }),

    /**
     * Cancel the running Crashing Out workflow for a coverage.
     */
    cancelCrashingOut: builder.mutation<
      CancelCrashingOutResponse,
      { matterId: string; coverageId: string; request?: CancelCrashingOutRequest }
    >({
      query: ({ matterId, coverageId, request = {} }) => ({
        url: `api/matters/${matterId}/coverage/${coverageId}/agentflow/crashing-out/cancel/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_result, _error, { coverageId }) => [
        { type: 'CrashingOutWorkflow', id: coverageId },
        { type: 'CrashingOutWorkflow', id: `history-${coverageId}` },
        { type: 'CrashingOutWorkflow', id: `reviews-${coverageId}` },
      ],
    }),

    /**
     * Get current Crashing Out workflow status.
     */
    getCrashingOutStatus: builder.query<
      CrashingOutStatusResponse,
      { matterId: string; coverageId: string; source?: CrashingOutSource }
    >({
      query: ({ matterId, coverageId, source }) => {
        const searchParams = new URLSearchParams();
        if (source) searchParams.set('source', source);
        const qs = searchParams.toString();
        return `api/matters/${matterId}/coverage/${coverageId}/agentflow/crashing-out/status/${qs ? `?${qs}` : ''}`;
      },
      providesTags: (_result, _error, { coverageId }) => [
        { type: 'CrashingOutWorkflow', id: coverageId },
      ],
    }),

    /**
     * List Crashing Out workflow runs for a coverage.
     */
    getCrashingOutHistory: builder.query<
      CrashingOutHistoryResponse,
      { matterId: string; coverageId: string; source?: CrashingOutSource }
    >({
      query: ({ matterId, coverageId, source }) => {
        const searchParams = new URLSearchParams();
        if (source) searchParams.set('source', source);
        const qs = searchParams.toString();
        return `api/matters/${matterId}/coverage/${coverageId}/agentflow/crashing-out/history/${qs ? `?${qs}` : ''}`;
      },
      providesTags: (_result, _error, { coverageId }) => [
        { type: 'CrashingOutWorkflow', id: `history-${coverageId}` },
      ],
    }),

    /**
     * List pending human-review forms for Crashing Out.
     */
    getCrashingOutPendingReviews: builder.query<
      CrashingOutPendingReviewsResponse,
      { matterId: string; coverageId: string; source?: CrashingOutSource }
    >({
      query: ({ matterId, coverageId, source }) => {
        const searchParams = new URLSearchParams();
        if (source) searchParams.set('source', source);
        const qs = searchParams.toString();
        return `api/matters/${matterId}/coverage/${coverageId}/agentflow/crashing-out/reviews/${qs ? `?${qs}` : ''}`;
      },
      providesTags: (_result, _error, { coverageId }) => [
        { type: 'CrashingOutWorkflow', id: `reviews-${coverageId}` },
      ],
    }),

    /**
     * Get status for a specific Crashing Out workflow run.
     */
    getCrashingOutRunStatus: builder.query<
      CrashingOutStatusResponse,
      { matterId: string; coverageId: string; runId: string; source?: CrashingOutSource }
    >({
      query: ({ matterId, coverageId, runId, source }) => {
        const searchParams = new URLSearchParams();
        if (source) searchParams.set('source', source);
        const qs = searchParams.toString();
        return `api/matters/${matterId}/coverage/${coverageId}/agentflow/crashing-out/runs/${runId}/${qs ? `?${qs}` : ''}`;
      },
      providesTags: (_result, _error, { runId }) => [
        { type: 'CrashingOutWorkflow', id: `run-${runId}` },
      ],
    }),

    /**
     * Get available LOA Chaser flow configurations with default email content.
     * Returns all flows with their steps and default email templates.
     */
    getLOAChaserFlows: builder.query<LOAChaserFlowsResponse, void>({
      query: () => 'api/agentflow/loa-chaser/flows/',
    }),

    // -------------------------------------------------------------------------
    // LOA Scan Endpoints (hidden admin page)
    // -------------------------------------------------------------------------

    /**
     * Get the most recent LOA scan (running preferred, then completed).
     */
    getLOAScanLatest: builder.query<LOAScanLatestResponse, void>({
      query: () => 'api/agentflow/loa-scan/latest/',
    }),

    /**
     * Start LOA email scan - fires off LLMBlock workflows for all eligible tasks.
     */
    startLOAScan: builder.mutation<LOAScanStartResponse, void>({
      query: () => ({
        url: 'api/agentflow/loa-scan/start/',
        method: 'POST',
      }),
    }),

    /**
     * Poll results for a previously started LOA scan.
     */
    getLOAScanResults: builder.query<
      LOAScanResultsResponse,
      { scanTs: number }
    >({
      query: ({ scanTs }) => `api/agentflow/loa-scan/results/?scan_ts=${scanTs}`,
    }),

    /**
     * Apply scan results as task updates for selected tasks.
     */
    applyLOAScanUpdates: builder.mutation<
      LOAScanApplyResponse,
      LOAScanApplyRequest
    >({
      query: (body) => ({
        url: 'api/agentflow/loa-scan/apply/',
        method: 'POST',
        body,
      }),
    }),

    // -------------------------------------------------------------------------
    // Records Request Evaluation Endpoints
    // -------------------------------------------------------------------------

    /**
     * Get current evaluation status for a treatment request.
     * Used for status line polling.
     */
    getRecordsRequestStatus: builder.query<
      RecordsRequestStatusResponse,
      { matterId: string; requestId: string }
    >({
      query: ({ matterId, requestId }) =>
        `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/status/`,
      providesTags: (_result, _error, { requestId }) => [
        { type: 'RecordsRequestWorkflow', id: requestId },
      ],
    }),

    /**
     * Get full evaluation details for the modal.
     */
    getRecordsRequestEvaluation: builder.query<
      RecordsRequestEvaluationDetail,
      { matterId: string; requestId: string; runId: string }
    >({
      query: ({ matterId, requestId, runId }) =>
        `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/evaluation/${runId}/`,
      providesTags: (_result, _error, { runId }) => [
        { type: 'RecordsRequestWorkflow', id: `eval-${runId}` },
      ],
    }),

    /**
     * Promote evaluation draft into the active submission flow.
     */
    promoteRecordsRequestPackage: builder.mutation<
      RecordsRequestPromoteResponse,
      { matterId: string; requestId: string; runId: string; request?: RecordsRequestPromoteRequest }
    >({
      query: ({ matterId, requestId, runId, request = {} }) => ({
        url: `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/evaluation/${runId}/promote/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'RecordsRequestWorkflow', id: requestId },
      ],
    }),

    /**
     * Start a new evaluation run (re-evaluate).
     */
    startRecordsRequestEvaluation: builder.mutation<
      RecordsRequestStartResponse,
      { matterId: string; requestId: string; request?: RecordsRequestStartRequest }
    >({
      query: ({ matterId, requestId, request = {} }) => ({
        url: `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/start/`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'RecordsRequestWorkflow', id: requestId },
      ],
    }),

    // -------------------------------------------------------------------------
    // Submission Attempt Endpoints
    // -------------------------------------------------------------------------

    /**
     * Get the canonical submission attempt detail for the modal.
     */
    getSubmissionAttemptDetail: builder.query<
      GetSubmissionAttemptDetailResponse,
      { matterId: string; requestId: string }
    >({
      query: ({ matterId, requestId }) =>
        `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/submission-attempt/`,
      providesTags: (_result, _error, { requestId }) => [
        { type: 'SubmissionAttempt', id: requestId },
        { type: 'RecordsRequestWorkflow', id: requestId },
      ],
    }),

    /**
     * Apply a manual resolution to the submission attempt (field, destination, signature).
     */
    patchSubmissionAttempt: builder.mutation<
      GetSubmissionAttemptDetailResponse,
      { matterId: string; requestId: string; body: SubmissionAttemptPatchRequest }
    >({
      query: ({ matterId, requestId, body }) => ({
        url: `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/submission-attempt/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'SubmissionAttempt', id: requestId },
      ],
    }),

    /**
     * Lightweight recompile after resolving blockers.
     */
    rebuildSubmissionAttempt: builder.mutation<
      GetSubmissionAttemptDetailResponse,
      { matterId: string; requestId: string }
    >({
      query: ({ matterId, requestId }) => ({
        url: `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/submission-attempt/rebuild/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'SubmissionAttempt', id: requestId },
        { type: 'RecordsRequestWorkflow', id: requestId },
      ],
    }),

    /**
     * Start a full re-evaluation workflow run against the canonical submission attempt.
     */
    reEvaluateSubmissionAttempt: builder.mutation<
      RecordsRequestStartResponse,
      { matterId: string; requestId: string }
    >({
      query: ({ matterId, requestId }) => ({
        url: `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/submission-attempt/re-evaluate/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'RecordsRequestWorkflow', id: requestId },
        { type: 'SubmissionAttempt', id: requestId },
      ],
    }),

    /**
     * Mark submission attempt as submitting and get the compiled package reference.
     */
    startSubmissionAttempt: builder.mutation<
      SubmissionAttemptStartResponse,
      { matterId: string; requestId: string }
    >({
      query: ({ matterId, requestId }) => ({
        url: `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/submission-attempt/start-submission/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'SubmissionAttempt', id: requestId },
        { type: 'RecordsRequestWorkflow', id: requestId },
      ],
    }),

    /**
     * Promote the ready submission attempt into the canonical HIPAA package and queue submission.
     */
    submitSubmissionAttempt: builder.mutation<
      SubmitSubmissionAttemptResponse,
      { matterId: string; requestId: string }
    >({
      query: ({ matterId, requestId }) => ({
        url: `api/matters/${matterId}/treatment-requests/${requestId}/agentflow/records-request/submission-attempt/submit/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { requestId }) => [
        { type: 'SubmissionAttempt', id: requestId },
        { type: 'RecordsRequestWorkflow', id: requestId },
      ],
    }),

    /**
     * Batch status for task-table badges.
     */
    getRecordsRequestBatchStatus: builder.mutation<
      RecordsRequestBatchStatusResponse,
      { matterId: string; treatmentRequestIds: string[] }
    >({
      query: ({ matterId, treatmentRequestIds }) => ({
        url: `api/matters/${matterId}/treatment-requests/agentflow/records-request/status-batch/`,
        method: 'POST',
        body: { treatment_request_ids: treatmentRequestIds },
      }),
    }),
  }),
});

export const {
  useStartOrganizeFilesMutation,
  useGetOrganizeFilesStatusQuery,
  useLazyGetOrganizeFilesStatusQuery,
  useSubmitOrganizeFilesApprovalMutation,
  useStartLOAChaserMutation,
  useGetLOAChaserStatusQuery,
  useLazyGetLOAChaserStatusQuery,
  useJumpToStepMutation,
  useGetLOAChaserHistoryQuery,
  useLazyGetLOAChaserHistoryQuery,
  useGetLOAChaserRunStatusQuery,
  useLazyGetLOAChaserRunStatusQuery,
  useCancelLOAChaserMutation,
  useGetLOAChaserFlowsQuery,
  useStartCrashingOutMutation,
  useCancelCrashingOutMutation,
  useGetCrashingOutStatusQuery,
  useLazyGetCrashingOutStatusQuery,
  useGetCrashingOutHistoryQuery,
  useLazyGetCrashingOutHistoryQuery,
  useGetCrashingOutPendingReviewsQuery,
  useLazyGetCrashingOutPendingReviewsQuery,
  useGetCrashingOutRunStatusQuery,
  useLazyGetCrashingOutRunStatusQuery,
  useGetLOAScanLatestQuery,
  useStartLOAScanMutation,
  useLazyGetLOAScanResultsQuery,
  useApplyLOAScanUpdatesMutation,
  useGetRecordsRequestStatusQuery,
  useGetRecordsRequestEvaluationQuery,
  usePromoteRecordsRequestPackageMutation,
  useStartRecordsRequestEvaluationMutation,
  useGetSubmissionAttemptDetailQuery,
  useLazyGetSubmissionAttemptDetailQuery,
  usePatchSubmissionAttemptMutation,
  useRebuildSubmissionAttemptMutation,
  useReEvaluateSubmissionAttemptMutation,
  useStartSubmissionAttemptMutation,
  useSubmitSubmissionAttemptMutation,
  useGetRecordsRequestBatchStatusMutation,
} = agentflowApiClient;
